import qz from "qz-tray";
import { AppDispatch } from "../../../store/store";
import { setConnectionState } from "../../../store/slices/qztray/qzTrayClientSlice";

/**
 * 🖨️ printTicketLogic
 *
 * Imprime usando QZ Tray con certificado público y firma remota.
 */
export const printTicketLogic = async (
  dispatch: AppDispatch,
  payload: any,
  printerName: string,
  commands: any[],
  handle: (err: any) => void,
  showAlert: (type: string, title: string, msg: string) => void,
  config: any,
  sign: (toSign: string) => Promise<string>
) => {
  console.groupCollapsed("🧩 QZ Tray | PRINT - Firmado");

  try {
    if (!printerName) {
      showAlert(
        "warning",
        "Impresora no especificada",
        "Debe indicar el nombre exacto de la impresora."
      );
      return;
    }

    if (!commands || !Array.isArray(commands) || commands.length === 0) {
      showAlert(
        "warning",
        "Comandos vacíos",
        "No hay datos válidos para imprimir."
      );
      return;
    }

    const cert = config?.certificateContent?.trim();

    if (!cert) {
      throw new Error(
        "No se recibió el certificado público requerido por QZ Tray."
      );
    }

    // ======================================================
    // 🔐 Certificado público
    // ======================================================
    qz.security.setCertificatePromise((resolve, reject) => {
      try {
        resolve(cert);
      } catch (error) {
        reject(error);
      }
    });

    // ======================================================
    // ✍️ Firma remota SHA256
    // ======================================================
    qz.security.setSignatureAlgorithm("SHA256");

    qz.security.setSignaturePromise((toSign) => {
      return (resolve, reject) => {
        sign(toSign)
          .then((signature) => {
            if (!signature || typeof signature !== "string") {
              reject("El backend no devolvió una firma válida.");
              return;
            }

            resolve(signature);
          })
          .catch((error) => {
            console.error("❌ Error firmando desde backend:", error);
            reject(error);
          });
      };
    });

    // ======================================================
    // 🔌 Conectar a QZ Tray
    // ======================================================
    if (!qz.websocket.isActive()) {
      const { host, port, sslEnabled } = config;

      const protocol = sslEnabled ? "wss" : "ws";
      const endpoint = `${protocol}://${host}:${port}`;

      await qz.websocket.connect({
        address: endpoint,
      });

      dispatch(
        setConnectionState({
          isConnected: true,
          endpoint,
        })
      );
    }

    // ======================================================
    // 🖨️ Configuración de impresión
    // ======================================================
    const copies = payload?.copies ?? 1;

    const printConfig = qz.configs.create(printerName, {
      encoding: "cp1252",
      copies,
      rasterize: false,
    });

    const rawText = commands.join("");

    const data = [
      {
        type: "raw",
        data: rawText,
      },
    ];

    // ======================================================
    // 🚀 Imprimir
    // ======================================================
    await qz.print(printConfig, data);

    showAlert(
      "success",
      "Impresión completada",
      `Ticket enviado a ${printerName}`
    );
  } catch (err: any) {
    console.error("❌ Error durante la impresión:", err);

    handle(err);

    if (
      err?.message?.includes("disconnected") ||
      err?.message?.includes("websocket")
    ) {
      dispatch(
        setConnectionState({
          isConnected: false,
          endpoint: null,
        })
      );
    }

    showAlert(
      "error",
      "Error de impresión",
      err?.message || "No se pudo enviar el ticket a QZ Tray."
    );
  } finally {
    console.groupEnd();
  }
};
